import React, { useState, useEffect } from 'react';
import {
  Autocomplete, Box, Button, Checkbox, CircularProgress, FormControlLabel, IconButton,
  InputAdornment, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import useFetch from '../../services/useFetch.js';
import GetData from '../../services/GetData.js';
import { recipeMeasurementConversions } from './UoMConversion.js';
import { UoMMenuOptions } from '../Helpers/UoMMenuOptions.js';
import { tokens } from '../../theme';

const apiUrl = 'api/invoicedetail';
const PACK_OPTIONS = ['Case', 'Individual'];
const RECIPE_UOM = ['oz', 'floz', 'tsp', 'tbsp', 'mL', 'grams', 'cup', 'units', 'none'];

const emptyRow = () => ({
  lineType: 'ingredient',
  ingredient: '',
  vendorItemId: null,
  ingredientId: null,
  ingredientObjectId: null,
  itemId: null,
  category: null,
  recipeUoM: '',
  itemUoM: '',
  itemSize: null,
  pack: '',
  packQuantity: null,
  packUnitQuantity: null,
  recipeIngQuantity: null,
  quantity: null,
  total: null,
});

function normalizeName(name) {
  return String(name || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function optionLabel(ing) {
  if (!ing) return '';
  const vendor = ing.vendor ? ` — ${ing.vendor}` : '';
  return `${ing.ingredient}${vendor}`;
}

function productOptionLabel(item) {
  if (!item) return '';
  if (typeof item === 'string') return item;
  const variant = item.variant && item.variant !== 'None' ? ` — ${item.variant}` : '';
  return `${item.itemName || ''}${variant}`;
}

function productUnits(row) {
  return Number(row.packQuantity || 0) * Number(row.packUnitQuantity || 1);
}

function ingredientQtyFromPacks(row) {
  const packs = Number(row.packQuantity) || 0;
  const perPack = Number(row.packUnitQuantity ?? 1) || 1;
  const recipePer = Number(row.recipeIngQuantity) || 0;
  return packs * perPack * recipePer;
}

/** Prefer exact id, then normalized name; lowest ingredientId wins for duplicates. */
function findInventoryMatch(list, { ingredientId, name }) {
  if (ingredientId != null && ingredientId !== '') {
    const byId = list.find((item) => String(item.ingredientId) === String(ingredientId));
    if (byId) return byId;
  }
  const target = normalizeName(name);
  if (!target) return null;
  const matches = list
    .filter((item) => normalizeName(item.ingredient) === target)
    .sort((a, b) => (Number(a.ingredientId) || 0) - (Number(b.ingredientId) || 0));
  return matches[0] || null;
}

export default function DynamicEntry({ invoiceSum, updateBody, invoiceOrigin, invOriginVendor, selectedVendor }) {
  const [load, setLoad] = useState(Boolean(invoiceOrigin));
  const [fndItemUoM, setFoundItemUoM] = useState({});
  const [rowData, setRowData] = useState([emptyRow()]);
  const [addedCosts, setAddedCosts] = useState({
    discount: 0.0,
    tax: 0.0,
    shipping: 0.0,
  });

  // All location inventory — so lines can match existing raw materials / products
  const { data: allIngredients, load: ingsLoad } = useFetch('api/ingredients', false);
  const { data: allProducts, load: productsLoad } = useFetch('api/menuItems', false);
  const inventoryList = Array.isArray(allIngredients) ? allIngredients : [];
  const productList = Array.isArray(allProducts) ? allProducts : [];

  useEffect(() => {
    if (invoiceOrigin && invOriginVendor) fetchInvoice();
    else setLoad(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!ingsLoad && !productsLoad) setLoad(false);
  }, [ingsLoad, productsLoad]);

  // Re-bind invoice lines to inventory once the list loads (normalized name / id)
  useEffect(() => {
    if (ingsLoad || !inventoryList.length || !rowData.some((r) => r.lineType !== 'product')) return;

    let cancelled = false;
    (async () => {
      let changed = false;
      let next = rowData.map((row) => ({ ...row }));
      for (let i = 0; i < next.length; i += 1) {
        if (next[i].lineType === 'product') continue;
        const matched = findInventoryMatch(inventoryList, {
          ingredientId: next[i].ingredientId,
          name: next[i].ingredient,
        });
        if (!matched) continue;
        const needsBind = next[i].ingredientId == null
          || String(next[i].ingredientId) !== String(matched.ingredientId)
          || normalizeName(next[i].ingredient) !== normalizeName(matched.ingredient)
          || !next[i].ingredientObjectId;
        if (!needsBind) continue;
        next = await applyMatchedIngredient(matched, i, next);
        // Preserve purchased qty / totals already on the line
        if (rowData[i].quantity != null) next[i].quantity = rowData[i].quantity;
        if (rowData[i].packQuantity != null) next[i].packQuantity = rowData[i].packQuantity;
        if (rowData[i].packUnitQuantity != null) next[i].packUnitQuantity = rowData[i].packUnitQuantity;
        if (rowData[i].pack) next[i].pack = rowData[i].pack;
        if (rowData[i].total != null) next[i].total = rowData[i].total;
        if (rowData[i].itemSize != null && rowData[i].ingredientId != null) {
          // Keep invoice item size when already matched previously
          next[i].itemSize = rowData[i].itemSize;
          next[i].recipeIngQuantity = rowData[i].recipeIngQuantity ?? next[i].recipeIngQuantity;
        }
        changed = true;
      }
      if (!cancelled && changed) pushRows(next, null, true);
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingsLoad, inventoryList.length]);

  const fetchInvoice = async () => {
    const { getData, error } = await GetData(
      apiUrl,
      { invnum: invoiceOrigin, vendorId: invOriginVendor },
      null, null, null,
    );

    if (getData) {
      const lines = Array.isArray(getData.ingredients) && getData.ingredients.length
        ? getData.ingredients
        : (Array.isArray(getData.items) ? getData.items : []);

      const normalized = lines.map((row) => ({
        lineType: row.lineType === 'product' ? 'product' : 'ingredient',
        ingredient: row.ingredient || row.item || '',
        vendorItemId: row.vendorItemId ?? row.vendorItemIdentifier ?? null,
        ingredientId: row.ingredientId ?? (row.lineType === 'product' ? null : row.itemId) ?? null,
        ingredientObjectId: row.ingredientObjectId ?? null,
        itemId: row.itemId ?? (row.lineType === 'product' ? row.ingredientId : null) ?? null,
        category: row.category ?? null,
        recipeUoM: row.recipeUoM || '',
        itemUoM: row.itemUoM || '',
        itemSize: row.itemSize ?? null,
        pack: row.pack ?? null,
        packQuantity: row.packQuantity ?? null,
        packUnitQuantity: row.packUnitQuantity ?? null,
        recipeIngQuantity: row.recipeIngQuantity ?? row.recipeQuantity ?? null,
        quantity: row.quantity ?? null,
        total: row.total ?? 0,
      }));

      const costs = {
        discount: Number(getData.discount) || 0,
        tax: Number(getData.tax) || 0,
        shipping: Number(getData.shipping) || 0,
      };
      setAddedCosts(costs);
      updateBody(costs.discount, 'discount', null, 'addedCost');
      updateBody(costs.tax, 'tax', null, 'addedCost');
      updateBody(costs.shipping, 'shipping', null, 'addedCost');

      setLoad(false);
      const next = normalized.length ? normalized : [emptyRow()];
      setRowData(next);
      updateBody(next, null, true);
      next.forEach((row, index) => {
        if (row.recipeUoM) findItemUoM(row.recipeUoM, index);
      });
    } else {
      setLoad(false);
    }
    if (error) console.log(error);
  };

  const pushRows = (next, index = null, del = false) => {
    setRowData(next);
    if (del) updateBody(next, null, true);
    else if (index != null) updateBody(next, index);
    else updateBody(next, null, true);
  };

  const applyMatchedIngredient = async (matched, index, next) => {
    if (!matched) return next;
    findItemUoM(matched.recipeUoM, index);
    const recipeUnitQuant = await recipeMeasurementConversions(
      matched.recipeUoM,
      matched.parUoM,
      matched.singleItemSize,
    );
    const packs = Number(next[index].packQuantity) || 0;
    const perPack = Number(next[index].packUnitQuantity ?? 1) || 1;
    const recipePer = Number(recipeUnitQuant) || 0;
    next[index] = {
      ...next[index],
      lineType: 'ingredient',
      ingredient: matched.ingredient,
      recipeUoM: matched.recipeUoM || next[index].recipeUoM,
      category: matched.category,
      itemUoM: matched.parUoM || next[index].itemUoM,
      itemSize: matched.singleItemSize ?? next[index].itemSize,
      recipeIngQuantity: recipePer,
      quantity: packs > 0 ? packs * perPack * recipePer : recipePer,
      ingredientId: matched.ingredientId,
      ingredientObjectId: matched._id ? String(matched._id) : next[index].ingredientObjectId,
      itemId: null,
      vendorItemId: matched.vendorIngredientID ?? next[index].vendorItemId,
    };
    return next;
  };

  const applyMatchedProduct = (matched, index, next) => {
    if (!matched) return next;
    next[index] = {
      ...next[index],
      lineType: 'product',
      ingredient: matched.itemName,
      category: matched.category ?? null,
      ingredientId: null,
      itemId: matched.itemId,
      recipeUoM: 'units',
      itemUoM: 'units',
      itemSize: 1,
      recipeIngQuantity: 1,
      pack: next[index].pack || 'Individual',
      packUnitQuantity: next[index].pack === 'Case'
        ? next[index].packUnitQuantity
        : (next[index].packUnitQuantity ?? 1),
      quantity: productUnits({
        ...next[index],
        pack: next[index].pack || 'Individual',
        packUnitQuantity: next[index].pack === 'Case'
          ? next[index].packUnitQuantity
          : (next[index].packUnitQuantity ?? 1),
      }) || 1,
    };
    return next;
  };

  const setLineType = (index, lineType) => {
    const next = [...rowData];
    const cleared = {
      ...emptyRow(),
      lineType,
    };
    if (lineType === 'product') {
      cleared.recipeUoM = 'units';
      cleared.itemUoM = 'units';
      cleared.itemSize = 1;
      cleared.recipeIngQuantity = 1;
      cleared.pack = 'Individual';
      cleared.packUnitQuantity = 1;
    }
    next[index] = cleared;
    pushRows(next, index);
  };

  const handleIngChange = async (e, val, reason, index) => {
    if (reason === 'clear') {
      const next = [...rowData];
      next[index] = { ...emptyRow(), lineType: next[index].lineType || 'ingredient' };
      if (next[index].lineType === 'product') {
        next[index].recipeUoM = 'units';
        next[index].itemUoM = 'units';
        next[index].itemSize = 1;
        next[index].recipeIngQuantity = 1;
        next[index].pack = 'Individual';
        next[index].packUnitQuantity = 1;
      }
      pushRows(next, index);
      return;
    }

    const next = [...rowData];
    const isProduct = next[index].lineType === 'product';

    if (isProduct) {
      if (val && typeof val === 'object' && val.itemName) {
        const updated = applyMatchedProduct(val, index, next);
        pushRows(updated, index);
        return;
      }
      const typed = typeof val === 'string' ? val : '';
      const namePart = typed.split(' — ')[0].trim();
      next[index] = { ...next[index], ingredient: namePart, itemId: null, ingredientId: null };
      const matched = productList.find((item) =>
        productOptionLabel(item).toLowerCase() === typed.toLowerCase()
        || item.itemName?.toLowerCase() === namePart.toLowerCase()
        || normalizeName(item.itemName) === normalizeName(namePart),
      );
      if (matched) {
        pushRows(applyMatchedProduct(matched, index, next), index);
      } else {
        pushRows(next, index);
      }
      return;
    }

    // Autocomplete onChange can pass the ingredient object (preferred) or a free-text string
    if (val && typeof val === 'object' && val.ingredient) {
      next[index] = {
        ...next[index],
        ingredient: val.ingredient,
        ingredientId: val.ingredientId,
        ingredientObjectId: val._id ? String(val._id) : null,
      };
      const updated = await applyMatchedIngredient(val, index, next);
      pushRows(updated, index);
      return;
    }

    const typed = typeof val === 'string' ? val : '';
    const namePart = typed.split(' — ')[0].trim();
    next[index] = {
      ...next[index],
      ingredient: namePart,
      ingredientId: null,
      ingredientObjectId: null,
    };

    const matched = findInventoryMatch(inventoryList, { name: namePart })
      || inventoryList.find((item) => optionLabel(item).toLowerCase() === typed.toLowerCase());

    if (matched) {
      const updated = await applyMatchedIngredient(matched, index, next);
      pushRows(updated, index);
    } else {
      pushRows(next, index);
    }
  };

  const handleInputChange = async (index, { target }, rowKey) => {
    const uin = target.value;
    const next = rowData.map((row, i) => (i === index ? { ...row } : row));
    next[index][rowKey] = uin;
    const isProduct = next[index].lineType === 'product';

    switch (rowKey) {
      case 'pack':
        if (uin === 'Individual') next[index].packUnitQuantity = 1.0;
        if (isProduct) {
          next[index].quantity = productUnits(next[index]);
          next[index].recipeIngQuantity = 1;
        }
        break;

      case 'packQuantity': {
        const packQty = Number(uin);
        next[index].packQuantity = packQty;
        if (isProduct) {
          next[index].quantity = productUnits(next[index]);
        } else {
          next[index].quantity = ingredientQtyFromPacks(next[index]);
        }
        break;
      }

      case 'packUnitQuantity': {
        const unitQty = Number(uin);
        next[index].packUnitQuantity = unitQty;
        if (isProduct) {
          next[index].quantity = productUnits(next[index]);
        } else {
          next[index].quantity = ingredientQtyFromPacks(next[index]);
        }
        break;
      }

      case 'recipeUoM':
        findItemUoM(uin, index);
        if (uin === 'none') {
          next[index] = {
            ...next[index],
            itemUoM: 'none',
            itemSize: 1.0,
            pack: 'Individual',
            packUnitQuantity: 1.0,
            packQuantity: 1.0,
            recipeIngQuantity: 1.0,
            quantity: 1.0,
          };
        }
        break;

      case 'itemSize': {
        const size = Number(uin);
        next[index].itemSize = size;
        const recipeUnitQuant = await recipeMeasurementConversions(
          next[index].recipeUoM,
          next[index].itemUoM,
          size,
        );
        next[index].recipeIngQuantity = Number(recipeUnitQuant);
        next[index].quantity = ingredientQtyFromPacks({
          ...next[index],
          recipeIngQuantity: Number(recipeUnitQuant),
        }) || Number(recipeUnitQuant);
        break;
      }

      case 'total':
        next[index].total = uin === '' ? null : Number(uin);
        break;

      default:
        break;
    }

    pushRows(next, index);
  };

  function findItemUoM(key, rowIndex) {
    const obj = UoMMenuOptions(key);
    setFoundItemUoM((prev) => ({
      ...prev,
      [rowIndex]: obj?.[key] || [],
    }));
  }

  const handleExtraCosts = ({ target }, rowKey) => {
    const value = target.value === '' ? '' : Number(target.value);
    setAddedCosts((cur) => ({ ...cur, [rowKey]: value }));
    updateBody(value === '' ? '' : Number(value), rowKey, null, 'addedCost');
  };

  const totalSum = () => {
    const tot = rowData.reduce((total, row) => total + (parseFloat(row.total) || 0), 0);
    return Number(
      tot
      + (parseFloat(addedCosts?.shipping) || 0)
      + (parseFloat(addedCosts?.tax) || 0)
      - (parseFloat(addedCosts?.discount) || 0),
    );
  };

  useEffect(() => {
    invoiceSum?.(Number(totalSum()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowData, addedCosts]);

  const onAdd = () => {
    const next = [...rowData, emptyRow()];
    pushRows(next, null, true);
  };

  const deleteRow = (index) => {
    if (rowData.length === 1) {
      const next = [emptyRow()];
      pushRows(next, null, true);
      return;
    }
    const next = rowData.filter((_, i) => i !== index);
    pushRows(next, null, true);
  };

  if (load || ((ingsLoad || productsLoad) && !invoiceOrigin && selectedVendor)) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', py: 5 }}>
        <CircularProgress size={26} />
      </Box>
    );
  }

  if (!selectedVendor && !invoiceOrigin) {
    return (
      <Typography sx={{ color: tokens.inkMuted, fontSize: 14, py: 1 }}>
        Select a vendor above to add line items.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack spacing={1.5}>
        {rowData.map((elem, indx) => {
          const isProduct = elem.lineType === 'product';
          return (
          <Box
            key={`line-${indx}`}
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${tokens.border}`,
              backgroundColor: tokens.bg,
            }}
          >
            <Stack spacing={1.25}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.ink, mr: 0.5 }}>
                  Line type
                </Typography>
                <FormControlLabel
                  control={(
                    <Checkbox
                      size="small"
                      checked={!isProduct}
                      onChange={() => setLineType(indx, 'ingredient')}
                    />
                  )}
                  label="Ingredient"
                  sx={{ mr: 1, color: tokens.inkMuted }}
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      size="small"
                      checked={isProduct}
                      onChange={() => setLineType(indx, 'product')}
                    />
                  )}
                  label="Product"
                  sx={{ color: tokens.inkMuted }}
                />
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'flex-start' }}>
                <Autocomplete
                  freeSolo={!isProduct}
                  fullWidth
                  size="small"
                  options={isProduct ? productList : inventoryList}
                  getOptionLabel={(opt) => (
                    typeof opt === 'string'
                      ? opt
                      : (isProduct ? productOptionLabel(opt) : optionLabel(opt))
                  )}
                  isOptionEqualToValue={(opt, val) => {
                    if (isProduct) {
                      if (typeof val === 'string') {
                        return productOptionLabel(opt) === val || opt.itemName === val;
                      }
                      return opt.itemId === val?.itemId;
                    }
                    if (typeof val === 'string') {
                      return normalizeName(opt.ingredient) === normalizeName(val.split(' — ')[0])
                        || normalizeName(optionLabel(opt)) === normalizeName(val);
                    }
                    return opt.ingredientId === val?.ingredientId;
                  }}
                  filterOptions={(options, { inputValue }) => {
                    const q = normalizeName(inputValue);
                    if (!q) return options;
                    if (isProduct) {
                      return options.filter((o) =>
                        normalizeName(o.itemName).includes(q)
                        || normalizeName(o.variant).includes(q)
                        || String(o.itemId).includes(q),
                      );
                    }
                    return options.filter((o) =>
                      normalizeName(o.ingredient).includes(q)
                      || normalizeName(o.vendor).includes(q)
                      || String(o.ingredientId).includes(q),
                    );
                  }}
                  value={elem.ingredient || ''}
                  onChange={(e, val, reason) => handleIngChange(e, val, reason, indx)}
                  onInputChange={(e, val, reason) => {
                    if (reason === 'input' || reason === 'clear') {
                      handleIngChange(e, val, reason, indx);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={isProduct ? 'Product' : 'Ingredient'}
                      placeholder={isProduct
                        ? 'Search existing products'
                        : 'Search inventory (pick a match)'}
                      error={!isProduct && Boolean(elem.ingredient) && elem.ingredientId == null}
                      helperText={
                        !isProduct && elem.ingredient && elem.ingredientId == null
                          ? 'Select an existing inventory item so stock updates on the right record'
                          : undefined
                      }
                    />
                  )}
                  sx={{ flex: 2, minWidth: 0 }}
                />
                <TextField
                  size="small"
                  label="Vendor item ID"
                  disabled={!elem.ingredient}
                  value={elem.vendorItemId || ''}
                  onChange={(e) => handleInputChange(indx, e, 'vendorItemId')}
                  sx={{ width: { xs: '100%', md: 140 }, flexShrink: 0 }}
                />
                <IconButton
                  aria-label="Remove line"
                  onClick={() => deleteRow(indx)}
                  sx={{ color: tokens.danger, alignSelf: { xs: 'flex-end', md: 'center' } }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                {!isProduct && (
                  <>
                    <TextField
                      select
                      size="small"
                      label="Recipe UoM"
                      disabled={!elem.ingredient}
                      value={elem.recipeUoM || ''}
                      onChange={(e) => handleInputChange(indx, e, 'recipeUoM')}
                      sx={{ minWidth: 110, flex: 1 }}
                    >
                      {RECIPE_UOM.map((option) => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      size="small"
                      label="Item UoM"
                      disabled={!elem.recipeUoM}
                      value={elem.itemUoM || ''}
                      onChange={(e) => handleInputChange(indx, e, 'itemUoM')}
                      sx={{ minWidth: 110, flex: 1 }}
                    >
                      {(fndItemUoM[indx] || []).map((option) => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      type="number"
                      label="Item size"
                      disabled={!elem.itemUoM}
                      value={elem.itemSize ?? ''}
                      onChange={(e) => handleInputChange(indx, e, 'itemSize')}
                      InputProps={{
                        endAdornment: elem.itemUoM ? (
                          <InputAdornment position="end">{elem.itemUoM}</InputAdornment>
                        ) : null,
                      }}
                      sx={{ minWidth: 110, flex: 1 }}
                    />
                  </>
                )}
                <TextField
                  select
                  size="small"
                  label="Pack"
                  disabled={isProduct ? !elem.ingredient : !elem.itemSize}
                  value={elem.pack || ''}
                  onChange={(e) => handleInputChange(indx, e, 'pack')}
                  sx={{ minWidth: 120, flex: 1 }}
                >
                  {PACK_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  type="number"
                  label="# Packs"
                  disabled={!elem.pack}
                  value={elem.packQuantity ?? ''}
                  onChange={(e) => handleInputChange(indx, e, 'packQuantity')}
                  sx={{ minWidth: 100, flex: 1 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Items / pack"
                  disabled={elem.pack !== 'Case' || !elem.packQuantity}
                  value={elem.packUnitQuantity ?? ''}
                  onChange={(e) => handleInputChange(indx, e, 'packUnitQuantity')}
                  sx={{ minWidth: 110, flex: 1 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Line total"
                  value={elem.total ?? ''}
                  onChange={(e) => handleInputChange(indx, e, 'total')}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  sx={{ minWidth: 120, flex: 1 }}
                />
              </Stack>
              {isProduct && elem.itemId != null && (
                <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
                  Matched product id {elem.itemId}
                  {elem.quantity != null ? ` · +${elem.quantity} for sale` : ''}
                </Typography>
              )}
              {!isProduct && elem.ingredientId != null && (
                <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
                  Matched inventory id {elem.ingredientId}
                  {elem.quantity != null ? ` · stock +${elem.quantity} ${elem.recipeUoM || ''}`.trim() : ''}
                </Typography>
              )}
              {!isProduct && elem.ingredient && elem.ingredientId == null && (
                <Typography sx={{ fontSize: 12, color: tokens.danger }}>
                  No inventory match — select from the list (create the ingredient under Raw Ingredients first if needed)
                </Typography>
              )}
            </Stack>
          </Box>
          );
        })}
      </Stack>

      <Box>
        <Button
          size="small"
          startIcon={<AddOutlinedIcon />}
          onClick={onAdd}
          sx={{ color: tokens.accent }}
        >
          Add line item
        </Button>
      </Box>

      <Box
        sx={{
          pt: 1.5,
          borderTop: `1px solid ${tokens.border}`,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
          Adjustments
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            size="small"
            type="number"
            label="Discount"
            value={addedCosts?.discount ?? ''}
            onChange={(e) => handleExtraCosts(e, 'discount')}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            sx={{ maxWidth: { sm: 160 } }}
          />
          <TextField
            size="small"
            type="number"
            label="Shipping"
            value={addedCosts?.shipping ?? ''}
            onChange={(e) => handleExtraCosts(e, 'shipping')}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            sx={{ maxWidth: { sm: 160 } }}
          />
          <TextField
            size="small"
            type="number"
            label="Tax"
            value={addedCosts?.tax ?? ''}
            onChange={(e) => handleExtraCosts(e, 'tax')}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            sx={{ maxWidth: { sm: 160 } }}
          />
        </Stack>
      </Box>
    </Stack>
  );
}
